import { ProductCard as SideBasketProductCard } from 'front-commons/ds';
import { createElement, useEffect, useRef, useState } from 'react';
import { ProductCardProps } from '../../interfaces';
import { AddToBasketButtonProps } from 'containers/AddToBasketButton/interfaces';
import { SuggestedProps } from 'services/basket/interfaces';
import { SideBasketProps } from 'front-commons/ds/components/ProductCard/variants/SideBasket/interfaces';
import { onViewBoniPointsAmount, updateCartBoniPoints, updateCartGTM, updateCartGTMWhithBoni } from 'shared/gtm';
import AddToBasketButton from 'containers/AddToBasketButton';
import { useNavigate } from 'react-router-dom';
import useDrawer from 'stores/drawer';
import useBoniVisibility from 'containers/Boni/hooks/useBoniVisibility';
import { calculateBoniPointsDelta } from 'containers/Boni/helpers';

export default function ProductCard({
	data: {
		id,
		ean13,
		valid,
		discount,
		imageURL: simpleImageURL,
		quantity: initialQuantity,
		brandName,
		hyperaCode,
		productType,
		description,
		unitDiscount,
		baseDiscount,
		invalidReason,
		productsInCombo,
		suggestionGroupId,
		unitPricePromotion,
		negotiatedDiscount,
		suggestionGroupName,
		totalPricePromotion,
		totalTaxSubstitution,
		negotiatedDiscountApplied,
	},
	sourceData: { industryName, businessUnitName, distributorId, distributorName },
	loadingBasketType,
	deletingProductId,
	hasEditBasketPermission,
	handleRemoveFromBasket,
}: ProductCardProps) {
	const [quantity, setQuantity] = useState<number>(initialQuantity);
	const [loadingType, setLoadingType] = useState<SideBasketProps['loadingType']>();

	const navigate = useNavigate();
	const { handleCloseDrawer } = useDrawer();

	const ean = ean13;
	const imageURL = simpleImageURL || productsInCombo?.[0]?.imageURL || '';

	const { boniPoints, showBoniPoints, formatPoints, levelName } = useBoniVisibility(totalPricePromotion);

	const previousPoints = useRef(boniPoints || 0);

	const notValid = valid === false || !!invalidReason;

	const maxNegotiatedDiscount = negotiatedDiscount + baseDiscount;

	const needDiscountApproved = negotiatedDiscountApplied ? discount > maxNegotiatedDiscount : false;

	const discountTagVariant = (() => {
		if (!negotiatedDiscountApplied || !discount) return 'default';
		if (needDiscountApproved) return 'negotiatedNeedsApproval';
		if (discount === baseDiscount) return 'default';
		return 'negotiatedApproved';
	})();

	const buildGtmProduct = (qty?: number) => ({
		ean13,
		description,
		price: unitPricePromotion,
		quantity: qty || 0,
		brandName,
		industryName,
		businessUnitName,
		discount: unitDiscount,
		affiliation: distributorName,
		combo: false,
	});

	const counterOptions: AddToBasketButtonProps & Partial<SuggestedProps> = {
		disabled: !!deletingProductId || loadingBasketType === 'refetch' || productType === 'COMBO' || notValid,
		productId: id,
		allowZero: true,
		hasPermission: hasEditBasketPermission,
		distributorId: distributorId,
		counterOptions: {
			width: '99px',
			height: '37px',
			fontSize: '14px',
			disabled: notValid,
			iconProps: { size: '16px', color: '--text-link' },
			'data-testid': 'basket-drawer-counter',
			hasPermission: hasEditBasketPermission && !hyperaCode && !notValid,
		},
		initialQuantity: quantity,
		suggestionGroupId,
		suggestionGroupName,
		basketLoadingImmediately: true,

		onItemChange: (value) => {
			setQuantity(value);
		},
		onUpdateBasketType({
			type,
			updateQuantity,
			oldQuantity,
			cartValue,
			productValue,
			changeSource,
			itemsCount,
			totalLiquid,
		}) {
			updateCartGTM(type, unitPricePromotion * quantity, [buildGtmProduct(updateQuantity)]);

			if (!showBoniPoints) return;

			const { freshBoniPoints, currentTotalPoints, previousTotalPoints } = calculateBoniPointsDelta({
				levelName,
				oldUnitPrice: unitPricePromotion,
				oldQuantity: Number(oldQuantity),
				newProductValue: productValue,
				totalLiquid,
			});

			updateCartGTMWhithBoni(type, quantity, [buildGtmProduct(quantity)], oldQuantity, cartValue);

			updateCartBoniPoints(quantity, cartValue, previousPoints.current, freshBoniPoints, [buildGtmProduct(quantity)]);

			onViewBoniPointsAmount({
				previousPoints: previousTotalPoints,
				currentPoints: currentTotalPoints,
				cartValue,
				itemsCount,
				changeSource,
			});

			previousPoints.current = freshBoniPoints;
		},
	};

	const productCardProps: SideBasketProps = {
		ean,
		name: description,
		price: totalPricePromotion,
		imageURL,
		isInvalid: notValid,
		hyperaCode,
		boniPoints: formatPoints,
		loadingType,
		showBoniPoints,
		staticStorageURL: import.meta.env.VITE_STATIC_STORAGE_BASE,
		totalTaxSubstitution: totalTaxSubstitution || 0,
		counterComponent: createElement(AddToBasketButton, counterOptions),
		// @ts-ignore
		discountTagVariant,
		hasEditBasketPermission,
		totalNegotiatedDiscount: discount || undefined,
		onRemoveClick() {
			handleRemoveFromBasket(id);
		},
		onEditPromotionClick() {
			handleCloseDrawer('side-basket-drawer');
			navigate(`/promocoes/${hyperaCode!}`);
		},
	};

	useEffect(() => {
		if (deletingProductId === id) {
			setLoadingType('DELETING');
			return;
		}

		if (deletingProductId || loadingBasketType) {
			setLoadingType('WAITING');
			return;
		}

		setLoadingType(undefined);
	}, [deletingProductId, loadingBasketType]);

	return <SideBasketProductCard key={id} variant="SIDE_BASKET" {...productCardProps} />;
}
